import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiCall } from '@/utils/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  FileText,
  ArrowRight,
  AlertCircle,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Trophy,
  Target,
  Users,
} from 'lucide-react';
import {
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';

interface SourceTag {
  tag_name: string;
  label: string;
  lead_count: number;
  won_count: number;
  conversion_rate: number;
}

interface TimeSeries {
  period: string;
  count: number;
}

interface FunnelStage {
  label: string;
  count: number;
}

interface TopSeller {
  kommo_user_id: number;
  name: string;
  leads_this_month: number;
  won_this_month: number;
}

interface OverviewData {
  total_leads: number;
  leads_this_month: number;
  leads_last_month: number;
  leads_change_percent: number;
  active_sellers: number;
  total_won: number;
  total_lost: number;
  conversion_rate: number;
  won_this_month: number;
  won_last_month: number;
  funnel: FunnelStage[];
  source_tags: SourceTag[];
  source_tags_config: Record<string, string>;
  top_sellers: TopSeller[];
  leads_per_month: TimeSeries[];
  leads_per_week: TimeSeries[];
  won_per_month: TimeSeries[];
}

const SOURCE_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];
const FUNNEL_COLORS = ['#94a3b8', '#3b82f6', '#f59e0b', '#8b5cf6', '#10b981', '#22c55e', '#ef4444'];

const KommoOverview: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await apiCall<{ success: boolean; data: OverviewData }>(
          token,
          '/kommo/analytics/overview'
        );
        if (res.success) setData(res.data);
      } catch (err) {
        toast.error('Erreur lors du chargement des analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-lg text-muted-foreground">Chargement des analytics...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-500 bg-red-50">
          <CardHeader>
            <div className="flex items-center text-red-600">
              <AlertCircle className="h-6 w-6 mr-2" />
              <CardTitle className="text-red-700">Erreur de chargement</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-red-600">Impossible de charger les donnees analytics.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalSourceLeads = data.source_tags.reduce((sum, t) => sum + t.lead_count, 0);
  const changeIsPositive = data.leads_change_percent >= 0;

  // Merge leads_per_month and won_per_month for combined chart
  const wonMap = new Map(data.won_per_month.map((w) => [w.period, w.count]));
  const combinedMonthly = data.leads_per_month.map((m) => ({
    period: m.period,
    leads: m.count,
    won: wonMap.get(m.period) || 0,
  }));

  // Funnel max for bar widths
  const funnelMax = Math.max(...data.funnel.map((f) => f.count), 1);

  const kpis = [
    {
      title: 'Leads ce mois',
      value: data.leads_this_month.toLocaleString('fr-FR'),
      icon: changeIsPositive ? TrendingUp : TrendingDown,
      color: changeIsPositive ? 'text-emerald-600' : 'text-red-600',
      sub: `${changeIsPositive ? '+' : ''}${data.leads_change_percent}% vs mois dernier`,
      subColor: changeIsPositive ? 'text-emerald-600' : 'text-red-500',
    },
    {
      title: 'Total Leads',
      value: data.total_leads.toLocaleString('fr-FR'),
      icon: FileText,
      color: 'text-blue-600',
      sub: `${data.active_sellers} commerciaux actifs`,
      subColor: 'text-slate-400',
    },
    {
      title: 'Leads Won',
      value: data.total_won.toLocaleString('fr-FR'),
      icon: Trophy,
      color: 'text-emerald-600',
      sub: `${data.won_this_month} ce mois (${data.won_last_month} mois dernier)`,
      subColor: 'text-slate-400',
    },
    {
      title: 'Taux de conversion',
      value: `${data.conversion_rate}%`,
      icon: Target,
      color: 'text-violet-600',
      sub: `${data.total_won} won / ${data.total_lost} lost`,
      subColor: 'text-slate-400',
    },
  ];

  return (
    <div className="px-4 py-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white p-5 rounded-xl border border-slate-200 relative overflow-hidden group">
        <div className="absolute top-0 right-0 p-6 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity">
          <BarChart3 className="h-24 w-24 rotate-12" />
        </div>
        <div className="z-10">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
            Kommo Analytics
          </h1>
          <p className="text-sm text-slate-500 font-medium mt-0.5">
            Vue d'ensemble des donnees <span className="text-primary font-semibold">CRM Kommo</span>
          </p>
        </div>
        <div className="z-10 flex gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/kommo/leads" className="flex items-center gap-1.5">
              Leads <ArrowRight className="h-3 w-3" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/kommo/sellers" className="flex items-center gap-1.5">
              Commerciaux <ArrowRight className="h-3 w-3" />
            </Link>
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.title} className="border-slate-200 hover:shadow transition-shadow duration-200 overflow-hidden group">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {kpi.title}
                </CardTitle>
                <div className={cn('p-1.5 rounded-md bg-slate-50 group-hover:bg-white transition-colors border border-slate-100', kpi.color)}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold tracking-tight text-slate-900">{kpi.value}</div>
                {kpi.sub && (
                  <p className={cn('text-xs mt-1 font-medium', kpi.subColor)}>
                    {kpi.sub}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Leads + Won per month (combined chart) */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base font-bold text-slate-900">Leads & Conversions par mois</CardTitle>
          <CardDescription className="text-xs text-slate-500">
            Leads crees vs leads gagnes - 12 derniers mois
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={combinedMonthly}>
                <defs>
                  <linearGradient id="colorLeadsArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                <Area type="monotone" dataKey="leads" stroke="#3b82f6" strokeWidth={2} fill="url(#colorLeadsArea)" name="Leads crees" />
                <Bar dataKey="won" fill="#22c55e" radius={[3, 3, 0, 0]} name="Leads gagnes" barSize={20} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Leads per week + Funnel + Top Sellers */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Leads per week */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-bold text-slate-900">Leads par semaine</CardTitle>
            <CardDescription className="text-xs text-slate-500">12 dernieres semaines</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.leads_per_week}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Leads" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Sales Funnel */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-bold text-slate-900">Entonnoir de vente</CardTitle>
            <CardDescription className="text-xs text-slate-500">Repartition actuelle des leads par etape</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.funnel.map((stage, i) => {
                const pct = (stage.count / funnelMax) * 100;
                const color = FUNNEL_COLORS[i % FUNNEL_COLORS.length];
                return (
                  <div key={stage.label} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-700">{stage.label}</span>
                      <span className="font-mono text-slate-900 font-semibold">
                        {stage.count.toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Top Sellers this month */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-bold text-slate-900">Top commerciaux</CardTitle>
                <CardDescription className="text-xs text-slate-500">Ce mois-ci</CardDescription>
              </div>
              <Button asChild variant="ghost" size="sm">
                <Link to="/kommo/sellers">
                  <Users className="h-4 w-4 mr-1" /> Voir tous
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {data.top_sellers && data.top_sellers.length > 0 ? (
              <div className="space-y-3">
                {data.top_sellers.map((seller, i) => {
                  const maxLeads = Math.max(...data.top_sellers.map((s) => s.leads_this_month), 1);
                  const pct = (seller.leads_this_month / maxLeads) * 100;
                  const medals = ['🥇', '🥈', '🥉'];
                  return (
                    <Link
                      key={seller.kommo_user_id}
                      to={`/kommo/sellers/${seller.kommo_user_id}`}
                      className="block group"
                    >
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-slate-700 group-hover:text-primary transition-colors">
                          {i < 3 ? `${medals[i]} ` : ''}{seller.name}
                        </span>
                        <div className="flex items-center gap-2">
                          {seller.won_this_month > 0 && (
                            <span className="text-[10px] text-emerald-600 font-semibold">
                              {seller.won_this_month} conv.
                            </span>
                          )}
                          <span className="font-mono text-slate-900 font-semibold text-xs">
                            {seller.leads_this_month}
                          </span>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500 group-hover:opacity-80"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: i === 0 ? '#3b82f6' : i === 1 ? '#6366f1' : '#8b5cf6',
                          }}
                        />
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Aucune donnee disponible</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Source Tags with conversion rates */}
      {data.source_tags && data.source_tags.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-bold text-slate-900">Performance par source</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Leads et taux de conversion par source d'acquisition ({totalSourceLeads.toLocaleString('fr-FR')} leads)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.source_tags.map((tag, i) => {
                const pct = totalSourceLeads > 0 ? (tag.lead_count / totalSourceLeads) * 100 : 0;
                const color = SOURCE_COLORS[i % SOURCE_COLORS.length];
                return (
                  <div key={tag.tag_name} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-700">{tag.label}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-emerald-600 font-semibold">
                          {tag.won_count} won ({tag.conversion_rate}%)
                        </span>
                        <span className="font-mono text-slate-900 font-semibold">
                          {tag.lead_count.toLocaleString('fr-FR')}
                        </span>
                        <span className="text-slate-400 w-12 text-right text-xs">{pct.toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default KommoOverview;
